/**
 * What a person sees when a screen fails: a calm title, one plain sentence,
 * the ways forward, and the error itself folded away under "Details".
 *
 * Two sizes. `content` sits in the app's content area, under the sidebar and
 * top bar, which keep working. `page` is the whole window, for a failure in
 * the shell itself; it uses no router and no provider, so it can be drawn
 * whatever broke.
 */
import { useId, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckIcon,
  ChevronDownIcon,
  Clock3Icon,
  CopyIcon,
  HouseIcon,
  Loader2Icon,
  RefreshCwIcon,
  RotateCcwIcon,
  SparklesIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScandiWordmark } from "@/components/brand/scandi-wordmark";
import { cn } from "@/lib/utils";
import { errorMessage } from "./classify";
import { useErrorCopy } from "./copy";
import type { FallbackProps } from "./error-boundary";
import { reloadNow } from "./stale";

export type ScreenSize = "page" | "content";

/** The error's own words, the page it happened on and when: what Copy puts on the clipboard. */
function report(error: unknown, at: Date): string {
  return [errorMessage(error), `${window.location.pathname}${window.location.search}`, at.toISOString()].join("\n");
}

/** The folded "Details": the error's message, a Copy button and the time. No stack. */
export function ErrorDetails({ error, at, align = "center" }: { error: unknown; at: Date; align?: "center" | "start" }) {
  const copy = useErrorCopy();
  const [copied, setCopied] = useState<"idle" | "done" | "failed">("idle");
  const message = errorMessage(error);
  const when = new Intl.DateTimeFormat(copy.locale, { dateStyle: "medium", timeStyle: "medium" }).format(at);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(report(error, at));
      setCopied("done");
    } catch {
      setCopied("failed");
    }
    window.setTimeout(() => setCopied("idle"), 2000);
  }

  return (
    <Collapsible className={cn("w-full", align === "center" ? "text-center" : "text-left")}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="group/details text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium transition-colors outline-none focus-visible:ring-3"
        >
          {copy.details}
          <ChevronDownIcon className="size-3.5 transition-transform duration-200 group-data-[state=open]/details:rotate-180" />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-1 data-[state=open]:duration-200">
        <div className="bg-muted/50 mt-2 rounded-lg border p-3 text-left">
          <div className="flex items-start gap-2">
            <p className="max-h-40 min-w-0 flex-1 overflow-auto font-mono text-xs/relaxed break-words whitespace-pre-wrap select-text">
              {message}
            </p>
            <Button type="button" variant="ghost" size="xs" className="-mt-0.5 -mr-1 shrink-0" onClick={() => void onCopy()}>
              {copied === "done" ? <CheckIcon data-icon="inline-start" /> : <CopyIcon data-icon="inline-start" />}
              {copied === "done" ? copy.copied : copied === "failed" ? copy.copyFailed : copy.copy}
            </Button>
          </div>
          <p className="text-muted-foreground mt-2 flex items-center gap-1.5 text-[11px] tabular-nums">
            <Clock3Icon className="size-3" aria-hidden />
            <time dateTime={at.toISOString()}>{when}</time>
          </p>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function Mark({ stale }: { stale: boolean }) {
  return (
    <div
      aria-hidden
      className={cn(
        "flex size-12 items-center justify-center rounded-2xl ring-1 ring-inset",
        stale ? "bg-brand/10 text-brand-ink ring-brand/20" : "bg-warning-muted text-warning ring-warning/20",
      )}
    >
      {stale ? <SparklesIcon className="size-5" /> : <TriangleAlertIcon className="size-5" />}
    </div>
  );
}

/** The panel itself: mark, words, actions, details. */
function Panel({ size, error, at, stale, reset }: FallbackProps & { size: ScreenSize }) {
  const copy = useErrorCopy();
  const titleId = useId();
  const title = stale ? copy.staleTitle : size === "page" ? copy.pageTitle : copy.contentTitle;
  const body = stale ? copy.staleBody : size === "page" ? copy.pageBody : copy.contentBody;
  const Heading = size === "page" ? "h1" : "h2";

  return (
    <section role="alert" aria-labelledby={titleId} className="flex w-full flex-col items-center gap-6 text-center">
      <Mark stale={stale} />
      <div className="flex flex-col gap-2">
        <Heading id={titleId} className="text-xl font-semibold tracking-tight text-balance">
          {title}
        </Heading>
        <p className="text-muted-foreground text-sm/relaxed text-balance">{body}</p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {stale ? (
          <Button size="lg" onClick={reloadNow}>
            <RefreshCwIcon data-icon="inline-start" />
            {copy.reload}
          </Button>
        ) : (
          <>
            <Button size="lg" onClick={reset}>
              <RotateCcwIcon data-icon="inline-start" />
              {copy.tryAgain}
            </Button>
            <Button size="lg" variant="outline" onClick={reloadNow}>
              <RefreshCwIcon data-icon="inline-start" />
              {copy.reload}
            </Button>
            {size === "page" ? (
              // A full load: whatever broke the shell starts over.
              <Button size="lg" variant="ghost" asChild>
                <a href="/">
                  <HouseIcon data-icon="inline-start" />
                  {copy.goHome}
                </a>
              </Button>
            ) : (
              <Button size="lg" variant="ghost" asChild>
                <Link to="/">
                  <HouseIcon data-icon="inline-start" />
                  {copy.goHome}
                </Link>
              </Button>
            )}
          </>
        )}
      </div>
      <ErrorDetails error={error} at={at} />
    </section>
  );
}

/** The whole-window frame, the same as the sign-in and waiting pages: the brand's glow, the wordmark, one card. */
export function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <main className="bg-background relative flex min-h-svh flex-col items-center justify-center overflow-hidden p-4">
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="brand-gradient absolute -top-40 left-1/2 size-[36rem] -translate-x-1/2 rounded-full opacity-10 blur-3xl" />
      </div>
      <div className="animate-in fade-in-0 slide-in-from-bottom-2 relative flex w-full max-w-md flex-col gap-8 duration-500 ease-out">
        <div className="flex justify-center">
          <ScandiWordmark className="h-7" gradient />
        </div>
        {children}
      </div>
    </main>
  );
}

/** A screen for a failure: in the content area, or the whole window. */
export function ErrorScreen({ size, ...props }: FallbackProps & { size: ScreenSize }) {
  if (size === "page") {
    return (
      <PageFrame>
        <Card className="shadow-lg shadow-black/5">
          <CardContent className="py-4">
            <Panel size="page" {...props} />
          </CardContent>
        </Card>
      </PageFrame>
    );
  }
  return (
    <div className="flex min-h-[min(70svh,36rem)] w-full flex-1 flex-col items-center justify-center px-4 py-12">
      <div className="animate-in fade-in-0 slide-in-from-bottom-2 w-full max-w-md duration-500 ease-out">
        <Panel size="content" {...props} />
      </div>
    </div>
  );
}

/**
 * The instant between an automatic reload being asked for and the page going:
 * nothing for a moment, then a quiet line, never an error.
 */
export function ReloadingScreen({ size }: { size: ScreenSize | "section" }) {
  const copy = useErrorCopy();
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "text-muted-foreground animate-in fade-in-0 fill-mode-both flex items-center justify-center gap-2 text-sm delay-300 duration-500",
        size === "page" && "bg-background min-h-svh",
        size === "content" && "min-h-[min(70svh,36rem)] w-full flex-1",
        size === "section" && "py-6",
      )}
    >
      <Loader2Icon className="size-4 animate-spin" aria-hidden />
      {copy.reloading}
    </div>
  );
}
